import React, { useContext, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid'
import { Upload } from 'antd'
import { LeadDto, LeadDtoKey } from 'api/Clients/Types/LeadDto'
import axios from 'axios'
import classNames from 'classnames'
import { FlexBox } from 'components/Box'
import { Modal } from 'components/CustomModal'
import { errorToast, infoToast, successToast, Toast, ToastContext } from 'components/Toast/Toast'
import _get from 'lodash/get'
import { GridFilterState, initialState } from 'store/grid'
import { useApiClient } from 'utils/api/ApiClient'
import { LeadsApiClient } from 'utils/api/leadsApi'
import { formatLocalDateUTC } from 'utils/date'

import './lead.scss'

import { NameDto } from 'api/Common/Types'
import { pageableSettings } from 'store/grid/grid.slice'

interface LeadsImportModalProps {
  visible: boolean

  setToast(toast?: Toast): void
  onResolve(): void
  onClose(): void
}

export const LeadsImportModal: React.FC<LeadsImportModalProps> = (props) => {
  const [paginationState, setPaginationState] = useState<GridFilterState>({ ...initialState() })
  const [fileInfo, setFileInfo] = useState<File>()
  const [leads, setLeads] = useState<LeadDto[]>([])
  const leadsApiClient = useApiClient(LeadsApiClient)
  const setToast = useContext(ToastContext)
  const isFileSelected = !!fileInfo

  const handleSelectFile = (file: File) => {
    if (file.type !== 'text/csv' && file.type !== 'application/vnd.ms-excel') {
      props.setToast(infoToast('Supported format is only csv.'))
      return false
    }
    setFileInfo(file)

    return false
  }

  const handleUploadFile = async () => {
    try {
      if (fileInfo) {
        const formData = new FormData()
        formData.append('files', fileInfo)

        const response = await leadsApiClient.uploadLeadsPreview(formData)
        setLeads(response)
      }
    } catch (e: unknown) {
      console.error(e)
      if (axios.isAxiosError(e)) {
        const err = e.response?.data?.message
        if (err) {
          setToast(errorToast(`Error: ${err}`))
        } else {
          setToast(errorToast('Error uploading leads file.'))
        }
      } else {
        setToast(errorToast('An unexpected error occurred.'))
      }
    }
  }

  const handleImportFile = async () => {
    try {
      if (!fileInfo) return

      const formData = new FormData()
      formData.append('files', fileInfo)
      await leadsApiClient.uploadLeads(formData)
      setTimeout(async () => {
        props.onResolve()
        setToast(successToast('Lead is imported successfully.'))
      }, 6000)
      setToast(infoToast('Lead is importing.'))

      props.onClose()
    } catch (e: unknown) {
      console.error(e)

      if (axios.isAxiosError(e)) {
        const err = e.response?.data?.message

        setToast(errorToast(err ? `Error: ${err}` : 'Error importing leads.'))
      } else {
        setToast(errorToast('An unexpected error occurred.'))
      }
    }
  }

  const handlePageChange = (event: GridPageChangeEvent) => {
    if (leads.length > 0) {
      setPaginationState({ ...initialState(), skip: event.page.skip, take: event.page.take })
    }
  }

  return (
    <Modal width={900} visible={props.visible} onClose={props.onClose}>
      <FlexBox margin={24}>
        <Upload beforeUpload={handleSelectFile} showUploadList={false}>
          <Button type='button'>{fileInfo?.name ? fileInfo?.name : <>Select File ...</>}</Button>
        </Upload>
        <Button type='button' onClick={handleUploadFile} disabled={!isFileSelected}>
          Upload
        </Button>
      </FlexBox>
      <Grid
        resizable
        data={leads.slice(paginationState.skip, paginationState.take + paginationState.skip)}
        skip={paginationState.skip}
        take={paginationState.take}
        total={leads.length}
        onPageChange={handlePageChange}
        pageable={pageableSettings}
        style={{ zIndex: 0 }}
      >
        <Column
          field={LeadDtoKey.title}
          title='Title'
          cell={LeadColumn}
          minResizableWidth={60}
          width={60}
        />
        <Column
          field={LeadDtoKey.firstName}
          title='First Name'
          cell={LeadColumn}
          minResizableWidth={160}
          width={160}
        />
        <Column
          field={LeadDtoKey.lastName}
          title='Last Name'
          cell={LeadColumn}
          minResizableWidth={160}
          width={160}
        />
        <Column
          field={LeadDtoKey.nativeName}
          title='Native Name'
          cell={LeadColumn}
          minResizableWidth={160}
          width={160}
        />
        <Column
          field={LeadDtoKey.middleName}
          title='Middle Name'
          cell={LeadColumn}
          minResizableWidth={160}
          width={160}
        />
        <Column
          field={LeadDtoKey.gender}
          title='Gender'
          cell={LeadColumn}
          minResizableWidth={80}
          width={80}
        />
        <Column
          field={LeadDtoKey.phoneNumber}
          title='Phone Number'
          cell={LeadColumn}
          minResizableWidth={160}
          width={160}
        />
        <Column
          field={LeadDtoKey.products}
          title='Product'
          cell={(props: GridCellProps) => {
            if (!props?.dataItem?.products?.length) {
              return <td />
            }

            return <td>{props.dataItem.products.map((p: NameDto) => p.name).join(',')}</td>
          }}
          minResizableWidth={160}
          width={160}
        />
        <Column
          field={LeadDtoKey.email}
          title='Email'
          cell={LeadColumn}
          minResizableWidth={200}
          width={200}
        />
        <Column
          field={LeadDtoKey.birthDay}
          title='BirthDay'
          cell={LeadColumn}
          minResizableWidth={180}
          width={180}
        />
        <Column
          field={LeadDtoKey.language}
          title='Language'
          cell={LeadColumn}
          minResizableWidth={120}
          width={120}
        />

        <Column
          field={LeadDtoKey.leadSource}
          title='Lead Source'
          cell={LeadColumn}
          minResizableWidth={120}
          width={120}
        />
        <Column
          field={LeadDtoKey.leadSourceDescription}
          title='Lead Source Desc.'
          cell={LeadColumn}
          minResizableWidth={160}
          width={160}
        />
        <Column
          field={LeadDtoKey.tickmillCompany}
          title='Entity'
          cell={LeadColumn}
          minResizableWidth={80}
          width={80}
        />
        <Column
          field={LeadDtoKey.type}
          title='Lead Type'
          cell={LeadColumn}
          minResizableWidth={160}
          width={160}
        />

        <Column
          field={LeadDtoKey.country}
          title='Country'
          cell={LeadColumn}
          minResizableWidth={160}
          width={160}
        />
        <Column
          field={LeadDtoKey.countryCode}
          title='Country Code'
          cell={LeadColumn}
          minResizableWidth={120}
          width={120}
        />
        <Column
          field={LeadDtoKey.city}
          title='City'
          cell={LeadColumn}
          minResizableWidth={160}
          width={160}
        />
        <Column
          field={LeadDtoKey.address}
          title='Address'
          cell={LeadColumn}
          minResizableWidth={160}
          width={160}
        />
        <Column
          field={LeadDtoKey.state}
          title='State'
          cell={LeadColumn}
          minResizableWidth={160}
          width={160}
        />
        <Column
          field={LeadDtoKey.postalCode}
          title='Postal Code'
          cell={LeadColumn}
          minResizableWidth={120}
          width={120}
        />

        <Column
          field={LeadDtoKey.optIn}
          title='Opt In'
          minResizableWidth={100}
          width={100}
          cell={(gridCellProps) => {
            const isInvalid = isLeadFieldInvalid(gridCellProps)

            return (
              <td
                className={classNames('table-columns__content', {
                  'lead-column-invalid': isInvalid,
                })}
              >
                <input type='checkbox' checked={gridCellProps.dataItem.optIn} readOnly />
              </td>
            )
          }}
        />

        <Column
          title='Campaign'
          minResizableWidth={120}
          width={120}
          children={[
            <Column
              title='Name'
              field={LeadDtoKey.campaign}
              cell={LeadColumn}
              minResizableWidth={120}
              width={120}
              className='table-columns__content'
            />,
            <Column
              title='Source'
              field={LeadDtoKey.campaignSource}
              cell={LeadColumn}
              minResizableWidth={120}
              width={120}
              className='table-columns__content'
            />,
            <Column
              title='Medium'
              field={LeadDtoKey.campaignMedium}
              cell={LeadColumn}
              minResizableWidth={120}
              width={120}
              className='table-columns__content'
            />,
            <Column
              title='Term'
              field={LeadDtoKey.campaignTerm}
              cell={LeadColumn}
              minResizableWidth={120}
              width={120}
              className='table-columns__content'
            />,
            <Column
              title='Content'
              field={LeadDtoKey.campaignContent}
              cell={LeadColumn}
              minResizableWidth={120}
              width={120}
              className='table-columns__content'
            />,
          ]}
        />

        <Column
          field={LeadDtoKey.referrer}
          title='Referrer'
          cell={LeadColumn}
          minResizableWidth={160}
          width={160}
        />
        <Column
          field={LeadDtoKey.referralCode}
          title='Referral Code'
          cell={LeadColumn}
          minResizableWidth={160}
          width={160}
        />
        <Column
          field={LeadDtoKey.sglAgent}
          title='SGL Agent'
          cell={LeadColumn}
          minResizableWidth={160}
          width={160}
        />
        <Column
          field={LeadDtoKey.teams}
          title='Teams'
          minResizableWidth={200}
          width={200}
          cell={(gridCellProps) => {
            const isInvalid = isLeadFieldInvalid(gridCellProps)

            const teams = gridCellProps.dataItem?.teams?.map((x: { name: string }) => x.name)
            if (teams?.length <= 0) {
              return (
                <td
                  className={classNames('table-columns__content', {
                    'lead-column-invalid': isInvalid,
                  })}
                />
              )
            }

            return (
              <td
                className={classNames('table-columns__content', {
                  'lead-column-invalid': isInvalid,
                })}
              >
                {(teams || []).map((name: string) => (
                  <div>{name}</div>
                ))}
              </td>
            )
          }}
        />
        <Column
          field={LeadDtoKey.assignee}
          title='Assignee Name'
          cell={LeadColumn}
          minResizableWidth={200}
          width={200}
        />

        <Column
          field={LeadDtoKey.created}
          title='Created'
          cell={(gridCellProps) => {
            const isInvalid = isLeadFieldInvalid(gridCellProps)

            return (
              <td
                className={classNames('table-columns__content', {
                  'lead-column-invalid': isInvalid,
                })}
              >
                {formatLocalDateUTC(gridCellProps.dataItem.created, true)}
              </td>
            )
          }}
          minResizableWidth={180}
          width={180}
        />
        <Column
          field={LeadDtoKey.updated}
          title='Updated'
          cell={(gridCellProps) => {
            const isInvalid = isLeadFieldInvalid(gridCellProps)

            return (
              <td
                className={classNames('table-columns__content', {
                  'lead-column-invalid': isInvalid,
                })}
              >
                {formatLocalDateUTC(gridCellProps.dataItem.updated, true)}
              </td>
            )
          }}
          minResizableWidth={180}
          width={180}
        />
      </Grid>
      <FlexBox margin='20px auto' alignItems='center' alignContent='center'>
        <Button onClick={handleImportFile} disabled={!isFileSelected}>
          Import
        </Button>
      </FlexBox>
    </Modal>
  )
}

const isLeadFieldInvalid = (gridCellProps: GridCellProps): boolean => {
  const lead: LeadDto = gridCellProps.dataItem
  const field: string = gridCellProps?.field || ''
  return (lead?.invalidFields || []).includes(field) || false
}

const LeadColumn = (gridCellProps: GridCellProps) => {
  const lead: LeadDto = gridCellProps.dataItem
  const field: string = gridCellProps?.field || ''
  const isInvalid = isLeadFieldInvalid(gridCellProps)

  return (
    <td
      className={classNames({
        'lead-column-invalid': isInvalid,
      })}
    >
      {_get(lead, field)}
    </td>
  )
}
